import React from 'react';

import { Box } from '@mui/material';
import { DateTime } from 'luxon';

import { formatMoney, sum } from '@clearverify/utils';

import { DashboardPlatformAdminFinancialDataResponse } from 'src/api';
import { useDashboardPrintView } from 'src/hooks/dashboard';
import { SxProp } from 'src/types/mui';

import { DashboardCard } from '../DashboardCard';

type DashboardCVAdminFinancialDataBlockProps = {
  loading: boolean;
  asOfDate: DateTime<true>;
  financialData: DashboardPlatformAdminFinancialDataResponse | undefined;
};

export const DashboardCVAdminFinancialDataBlock: React.FC<
  DashboardCVAdminFinancialDataBlockProps
> = ({ asOfDate, loading, financialData }) => {
  const { isPrintView } = useDashboardPrintView();

  const showValueOfRecord = React.useMemo(
    () => asOfDate.toUTC().hasSame(DateTime.utc(), 'day'),
    [asOfDate],
  );

  return (
    <Box sx={dashboardCVAdminFinancialDataBlockBoxStyles(isPrintView, !showValueOfRecord)}>
      <DashboardCard
        title="Commercial Verifications"
        description={`VOI: ${financialData?.voi || 0} VOE: ${financialData?.voe || 0} Reverification: ${financialData?.reverification || 0}`}
        value={`${financialData?.commercialVerifications || 0} Total`}
        loading={loading}
        valueTextProps={{ variant: 'h5' }}
      />
      {showValueOfRecord && (
        <DashboardCard
          title="Value of Record"
          description="A breakdown of revenue by number of active records"
          value={formatMoney(financialData?.valueOfRecord)}
          loading={loading}
          valueTextProps={{ variant: 'h5' }}
        />
      )}
      <DashboardCard
        title="Clear Verify Revenue"
        description="From Commercial Verifications"
        value={formatMoney(financialData?.platformRevenue)}
        loading={loading}
        valueTextProps={{ variant: 'h5' }}
      />
      <DashboardCard
        title="Revenue Share Total"
        description={`Paid: ${formatMoney(financialData?.revenueSharePaid)} Owed: ${formatMoney(financialData?.revenueShareOwed)}`}
        value={formatMoney(sum(financialData?.revenueSharePaid, financialData?.revenueShareOwed))}
        loading={loading}
        valueTextProps={{ variant: 'h5' }}
      />
    </Box>
  );
};

const dashboardCVAdminFinancialDataBlockBoxStyles =
  (isPrintView: boolean, hideOneColumn: boolean): SxProp =>
  theme => ({
    display: 'grid',
    mt: 4,
    gap: 2,
    gridTemplateColumns: `repeat(${hideOneColumn ? '3' : '4'}, 1fr)`,
    overflow: 'auto',

    [theme.breakpoints.down('lg')]: !isPrintView
      ? {
          gridTemplateColumns: 'repeat(2, 1fr)',
        }
      : {},

    [theme.breakpoints.down('md')]: !isPrintView
      ? {
          gridTemplateColumns: 'none',
        }
      : {},
  });
